// Measured gap edges (phase SC5): the upper and lower edges of the zero-conductance region of a measured map, uploaded
// as a file and drawn as dots on a computed map (the starting point for fitting a model to an experiment). Formats:
//   JSON  {"panels": {"Bz": {"X": [...], "upper": [...], "lower": [...], "bias_offset_mV": 0.08, "x_label": "B_z (T)"}, ...}}
//         (or "panels" as a list of objects with "name"; or one panel object {"X", "upper", "lower"})
//   CSV   a header row with columns X, upper, lower (also V_upper / V_lower, up / lo) and optionally panel; or two columns
//         X, V (edge points: V > 0 upper, V < 0 lower). Empty cells / null: no edge at that X.
export interface EdgePanel {
  name: string;
  X: number[];
  upper: (number | null)[];
  lower: (number | null)[];
  bias_offset_mV?: number;        // the measurement's bias offset (measured bias minus the true one), taken off before plotting
  x_label?: string;
}
export interface EdgeSet { panels: EdgePanel[] }

const num = (x: unknown): number | null => {
  if (x === null || x === undefined || x === "") return null;
  const v = typeof x === "number" ? x : Number(String(x).trim());
  return Number.isFinite(v) ? v : null;
};

function panelOf(name: string, o: Record<string, unknown>): EdgePanel {
  const X = (o.X ?? o.x) as unknown[];
  const up = (o.upper ?? o.up ?? o.V_upper) as unknown[] | undefined;
  const lo = (o.lower ?? o.lo ?? o.V_lower) as unknown[] | undefined;
  if (!Array.isArray(X) || (!Array.isArray(up) && !Array.isArray(lo)))
    throw new Error(`panel "${name}": needs X and upper and/or lower (arrays)`);
  const n = X.length;
  const col = (a: unknown[] | undefined) => (Array.isArray(a) ? a : []).slice(0, n).map(num).concat(Array(Math.max(0, n - (a?.length ?? 0))).fill(null));
  const xs = X.map(num);
  if (xs.some((x) => x === null)) throw new Error(`panel "${name}": X must be numbers`);
  const p: EdgePanel = { name, X: xs as number[], upper: col(up), lower: col(lo) };
  const off = num(o.bias_offset_mV);
  if (off !== null) p.bias_offset_mV = off;
  if (typeof o.x_label === "string") p.x_label = o.x_label;
  return p;
}

function parseJson(t: string): EdgeSet {
  const o = JSON.parse(t);
  if (o && typeof o === "object" && "panels" in o) {
    const ps = o.panels;
    if (Array.isArray(ps)) return { panels: ps.map((p, i) => panelOf(String(p.name ?? `panel ${i + 1}`), p)) };
    if (ps && typeof ps === "object") return { panels: Object.entries(ps).map(([k, v]) => panelOf(k, v as Record<string, unknown>)) };
  }
  if (o && typeof o === "object") return { panels: [panelOf(String(o.name ?? "edges"), o)] };
  throw new Error("JSON edges: an object with panels, or one panel {X, upper, lower}");
}

const HEAD = {
  x: /^(x|sweep|b|b_t|angle|theta|phi|vg|v_g|gate|field)\b/i,
  upper: /^(upper|up|top|v_upper|v_up|edge_upper)\b/i,
  lower: /^(lower|lo|low|bottom|v_lower|v_lo|edge_lower)\b/i,
  panel: /^(panel|name|map|sweep_name)$/i,
};

function parseCsv(t: string): EdgeSet {
  const rows = t.split(/\r?\n/).map((l) => l.trim()).filter((l) => l && !l.startsWith("#")).map((l) => l.split(/[,;\t]|\s{2,}/).map((c) => c.trim()));
  if (rows.length < 2) throw new Error("CSV edges: a header row and at least one row of values");
  const head = rows[0];
  const iP = head.findIndex((h) => HEAD.panel.test(h));
  const iU = head.findIndex((h) => HEAD.upper.test(h)), iL = head.findIndex((h) => HEAD.lower.test(h));
  let iX = head.findIndex((h, i) => i !== iP && HEAD.x.test(h));
  const body = rows.slice(1);
  const groups = new Map<string, string[][]>();
  for (const r of body) {
    const k = iP >= 0 ? r[iP] || "edges" : "edges";
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k)!.push(r);
  }
  const panels: EdgePanel[] = [];
  if (iU >= 0 || iL >= 0) {
    if (iX < 0) iX = head.findIndex((_, i) => i !== iP && i !== iU && i !== iL);
    for (const [name, rs] of groups) {
      const ok = rs.filter((r) => num(r[iX]) !== null);
      panels.push({ name, X: ok.map((r) => num(r[iX])!), upper: ok.map((r) => (iU >= 0 ? num(r[iU]) : null)),
        lower: ok.map((r) => (iL >= 0 ? num(r[iL]) : null)) });
    }
  } else {
    const cols = head.map((_, i) => i).filter((i) => i !== iP);
    if (cols.length !== 2) throw new Error("CSV edges: columns X, upper, lower (and optionally panel), or two columns X, V");
    const [cx, cv] = cols;
    for (const [name, rs] of groups) {
      const pts = rs.map((r) => [num(r[cx]), num(r[cv])] as const).filter(([x, v]) => x !== null && v !== null) as [number, number][];
      panels.push({ name, X: pts.map(([x]) => x), upper: pts.map(([, v]) => (v >= 0 ? v : null)), lower: pts.map(([, v]) => (v < 0 ? v : null)) });
    }
  }
  if (!panels.some((p) => p.X.length)) throw new Error("CSV edges: no rows with numbers");
  return { panels };
}

export function parseEdges(text: string): EdgeSet {
  const t = text.trim();
  if (!t) throw new Error("The file is empty.");
  return t.startsWith("{") || t.startsWith("[") ? parseJson(t) : parseCsv(t);
}

/** The panel that most likely belongs to a computed map (by plane, direction or gate), else the first. */
export function guessPanel(r: Record<string, any>, set: EdgeSet): number {
  const names = set.panels.map((p) => p.name.toLowerCase());
  const find = (re: RegExp) => names.findIndex((n) => re.test(n));
  let k = -1;
  if (r.mode === "gate" || Array.isArray(r.Vg)) k = find(/gate|cb|diamond|vg/);
  else if (r.variable === "angle") {
    const pl = String(r.label ?? "").match(/\b(XY|XZ|YZ)\b/i)?.[1]?.toLowerCase();
    if (pl) k = find(new RegExp(`\\b${pl}\\b|${pl}$`));
  } else if (r.variable === "B") {
    const d = String(r.label ?? "").match(/along ([xyz])\b/i)?.[1]?.toLowerCase();
    if (d) k = find(new RegExp(`^b_?${d}$|^b${d}\\b|\\|b\\|.*${d}`));
  }
  return k >= 0 ? k : 0;
}

export interface OverlayOptions { xShift: number; xScale: number; offset: number }

/** The computed map (sweep across, bias up, like the measured figures) with the measured edges as dots. */
export function overlayFigure(r: Record<string, any>, panel: EdgePanel | null, o: OverlayOptions) {
  const gate = r.mode === "gate" || Array.isArray(r.Vg);
  const G = (r.temperature_K > 0 && r.G_T && !gate ? r.G_T : r.G) as number[][];
  const X = (gate ? r.Vg : r.X) as number[];
  const V = r.V as number[];
  const z = V.map((_, j) => G.map((row) => row[j]));
  const data: unknown[] = [{ type: "heatmap", x: X, y: V, z, colorscale: "Viridis", colorbar: { title: { text: "2e²/h" } },
    hovertemplate: `${gate ? "V_g" : r.variable} %{x:.4g}<br>V %{y:.4g} mV<br>G %{z:.4f}<extra></extra>`, name: "computed" }];
  if (panel) {
    const xs = panel.X.map((x) => o.xScale * (x - o.xShift));
    const pts = (ys: (number | null)[]) => {
      const keep = ys.map((y, i) => [xs[i], y] as const).filter(([, y]) => y !== null) as [number, number][];
      return { x: keep.map(([x]) => x), y: keep.map(([, y]) => y - o.offset) };
    };
    const mk = { size: 6, color: "#ff5a36", line: { color: "#ffffff", width: 1 } };
    data.push({ type: "scatter", mode: "markers", name: `${panel.name}: upper edge (measured)`, ...pts(panel.upper), marker: { ...mk, symbol: "circle" } });
    data.push({ type: "scatter", mode: "markers", name: `${panel.name}: lower edge (measured)`, ...pts(panel.lower), marker: { ...mk, symbol: "diamond" } });
  }
  const xl = gate ? "gate V_g (mV)" : (r.label ?? r.variable);
  return { data, layout: { title: { text: `${r.name || "Computed map"}${panel ? ` · measured edges: ${panel.name}` : ""}` },
    xaxis: { title: { text: xl } }, yaxis: { title: { text: "bias V (mV)" } }, legend: { orientation: "h", y: -0.22 } } };
}

/** The edges as the CSV this module reads (panel, X, upper, lower), e.g. to keep a copy or edit it. */
export function edgesCsv(set: EdgeSet): string {
  const lines = ["panel,X,upper,lower"];
  for (const p of set.panels)
    p.X.forEach((x, i) => lines.push([p.name, x, p.upper[i] ?? "", p.lower[i] ?? ""].join(",")));
  return lines.join("\n") + "\n";
}
